import { z } from 'zod';
import { uuid } from './contratos.js';
import { TIPOS_MEDIDOR } from './enums.js';

/**
 * Medidores — §11.
 *
 * Un medidor mide una magnitud de un activo a lo largo del tiempo (ej.
 * potencia de un cabezal de depilación IPL/láser): cada lectura es un punto,
 * y la tendencia entre lecturas —no una lectura aislada— es la señal de
 * desgaste que le importa al operador. Por eso las lecturas son de solo
 * lectura una vez creadas (ver `0008_inmutabilidad.sql`): corregir un punto
 * de la serie borraría la evidencia de qué se vio en su momento.
 *
 * El CHECK de `frecuencia_unidad` en la base (`0005_fases3a7.sql`) no admite
 * `'NINGUNO'` —a diferencia de `UNIDADES_FRECUENCIA` completo, pensado para
 * la recurrencia de OT donde "no se repite" es una opción válida—, así que
 * este schema usa su propia lista recortada en vez de reutilizar esa unión.
 */
export const UNIDADES_FRECUENCIA_MEDIDOR = ['HORAS', 'DIAS', 'SEMANAS', 'MESES', 'ANIOS'] as const;
export type UnidadFrecuenciaMedidor = (typeof UNIDADES_FRECUENCIA_MEDIDOR)[number];

/**
 * §11, alta simplificada: el operador solo nombra el medidor al crearlo desde
 * la máquina seleccionada. Unidad de medida, frecuencia de recordatorio y
 * rango esperado quedan disponibles para completarse después (ej. desde una
 * edición futura), no como fricción de entrada — de ahí que aquí sean
 * opcionales aunque la tabla los soportaba como obligatorios en su diseño
 * original (`0022_medidores_simplificados.sql` relajó esas columnas).
 */
export const medidorSchema = z
  .object({
    nombre: z.string().trim().min(1, 'Requerido').max(200),
    tipo: z.enum(TIPOS_MEDIDOR).default('MANUAL'),
    descripcion: z.string().trim().max(2000).optional(),
    unidadMedidaId: uuid.optional(),
    activoId: uuid,
    frecuenciaValor: z.coerce.number().int().min(1).max(3650).optional(),
    frecuenciaUnidad: z.enum(UNIDADES_FRECUENCIA_MEDIDOR).optional(),
    fechaCalibracion: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD').optional(),
    valorMinimoEsperado: z.coerce.number().optional(),
    valorMaximoEsperado: z.coerce.number().optional(),
  })
  .superRefine((datos, ctx) => {
    if (
      datos.valorMinimoEsperado !== undefined &&
      datos.valorMaximoEsperado !== undefined &&
      datos.valorMaximoEsperado < datos.valorMinimoEsperado
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valorMaximoEsperado'],
        message: 'Debe ser mayor o igual al valor mínimo esperado',
      });
    }
  });
export type MedidorEntrada = z.infer<typeof medidorSchema>;

/** Alta masiva: un medidor con este nombre en cada activo del tipo indicado. */
export const medidorPorTipoSchema = z.object({
  nombre: z.string().trim().min(1, 'Requerido').max(200),
  tipoActivoId: uuid,
});
export type MedidorPorTipoEntrada = z.infer<typeof medidorPorTipoSchema>;

export const filtroMedidorSchema = z.object({
  activoId: uuid.optional(),
  ubicacionId: uuid.optional(),
  tipoActivoId: uuid.optional(),
});
export type FiltroMedidor = z.infer<typeof filtroMedidorSchema>;

export const lecturaMedidorSchema = z.object({
  valor: z.coerce.number(),
  /**
   * Literal `AAAA-MM-DD`, no un timestamp: la medición se registra por día,
   * y el operador puede cargar una de una fecha pasada (ej. la olvidó
   * ingresar el mismo día). Sin literal, `z.coerce.date()` corre la fecha un
   * día según el huso horario del proceso — el mismo bug que ya se corrigió
   * en el calendario de OT (HU-06) y en `aprobarSolicitudSchema` (HU-12).
   */
  fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD').optional(),
});
export type LecturaMedidorEntrada = z.infer<typeof lecturaMedidorSchema>;

/** Igual que el alta, pero sin fecha: la fecha del hecho medido no se toca al corregir. */
export const editarLecturaMedidorSchema = z.object({
  valor: z.coerce.number(),
  nota: z.string().trim().max(2000).optional(),
});
export type EditarLecturaMedidorEntrada = z.infer<typeof editarLecturaMedidorSchema>;

export interface Medidor {
  id: string;
  numero: number;
  nombre: string;
  tipo: (typeof TIPOS_MEDIDOR)[number];
  descripcion: string | null;
  unidadMedidaId: string | null;
  unidadMedida: string | null;
  activoId: string;
  activoNombre: string;
  ubicacionId: string | null;
  ubicacionNombre: string | null;
  frecuenciaValor: number | null;
  frecuenciaUnidad: UnidadFrecuenciaMedidor | null;
  proximaLectura: string | null;
  fechaCalibracion: string | null;
  valorMinimoEsperado: number | null;
  valorMaximoEsperado: number | null;
  ultimaLectura: { valor: number; fechaHora: string } | null;
}

export interface LecturaMedidor {
  id: string;
  valor: number;
  fechaHora: string;
  origen: 'MANUAL' | 'PROCEDIMIENTO' | 'AUTOMATIZADO';
  nota: string | null;
  registradoPor: string | null;
  /** No nulo si el valor o la nota se corrigieron después del alta. */
  editadoEl: string | null;
  /** No nulo si el registro fue anulado; ya no cuenta para gráfica ni tendencia. */
  anuladoEl: string | null;
}

/** Lectura de cualquier medidor de un activo, para la sección de mediciones de su ficha. */
export interface MedicionActivo extends LecturaMedidor {
  medidorId: string;
  medidorNombre: string;
  unidadMedida: string | null;
}
